import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { unwrapList } from '../lib/unwrap'

export interface ReportTemplate {
  id: string
  name: string
  description: string
  report_type: string
  default_config: Record<string, unknown>
  created_at: string
}

export interface ReportSchedule {
  id: string
  tenant_id: string
  name: string
  template_id?: string
  report_type: string
  config: Record<string, unknown>
  schedule: string // daily | weekly | monthly | once
  email_to: string
  next_run_at?: string
  last_run_at?: string
  enabled: boolean
  created_by: string
  created_at: string
}

export interface ReportFile {
  id: string
  tenant_id: string
  schedule_id?: string
  name: string
  report_type: string
  format: string
  file_size: number
  created_at: string
}

export function useReportTemplates() {
  return useQuery({
    queryKey: ['report-templates'],
    queryFn: () => api.get('/reports/templates').then((r) => unwrapList<ReportTemplate>(r.data)),
  })
}

export function useReportSchedules() {
  return useQuery({
    queryKey: ['report-schedules'],
    queryFn: () => api.get('/reports/schedules').then((r) => unwrapList<ReportSchedule>(r.data)),
  })
}

export function useCreateSchedule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<ReportSchedule>) =>
      api.post('/reports/schedules', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['report-schedules'] }),
  })
}

export function useUpdateSchedule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<ReportSchedule> & { id: string }) =>
      api.put(`/reports/schedules/${id}`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['report-schedules'] }),
  })
}

export function useDeleteSchedule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/reports/schedules/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['report-schedules'] }),
  })
}

export function useGenerateReport() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { report_type: string; config?: Record<string, unknown>; name?: string }) =>
      api.post('/reports/generate', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['report-files'] }),
  })
}

export function useReportFiles() {
  return useQuery({
    queryKey: ['report-files'],
    queryFn: () => api.get('/reports/files').then((r) => unwrapList<ReportFile>(r.data)),
  })
}

export async function downloadReportFile(id: string) {
  const res = await api.get(`/reports/files/${id}/download`, { responseType: 'blob' })
  const contentDisposition = res.headers['content-disposition']
  let filename = `report-${id}.csv`
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?([^";\n]+)"?/)
    if (match) filename = match[1]
  }
  const url = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
